    // Optional Y-axis formatter function (for values like 1M, 1.2M)
    yAxisFormatter: {
      type: Function,
      default: null,
    },
    // Explicit chart height in px; for horizontal charts defaults to auto (30px per row)
    height: {
      type: Number,
      default: null,
    },
<template>
  <div class="bd-bar-chart-wrapper">
    <div v-if="!hasData" class="text-muted small">No data available.</div>
    <div v-else ref="chartEl" class="bd-bar-chart"></div>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'

const GOOGLE_LOADER_SRC = 'https://www.gstatic.com/charts/loader.js'

function loadGoogleCharts() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('Window is undefined'))
    }

    if (window.google && window.google.charts && window.google.visualization) {
      window.google.charts.load('current', { packages: ['corechart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
      return
    }

    const existing = document.querySelector(`script[src="${GOOGLE_LOADER_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => {
        window.google.charts.load('current', { packages: ['corechart'] })
        window.google.charts.setOnLoadCallback(() => resolve(window.google))
      })
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Charts')))
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_LOADER_SRC
    script.async = true
    script.onload = () => {
      if (!window.google || !window.google.charts) {
        reject(new Error('Google Charts not available'))
        return
      }
      window.google.charts.load('current', { packages: ['corechart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
    }
    script.onerror = () => reject(new Error('Failed to load Google Charts'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'BDBarChart',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    categoryKey: {
      type: String,
      required: true,
    },
    valueKey: {
      type: String,
      required: true,
    },
    valueLabel: {
      type: String,
      default: 'Value',
    },
    orientation: {
      // 'column' (vertical) or 'bar' (horizontal)
      type: String,
      default: 'column',
    },
    // Optional multi-series configuration for stacked charts
    // e.g. [{ key: 'wins', label: 'Wins' }, { key: 'losses', label: 'Losses' }]
    series: {
      type: Array,
      default: null,
    },
    // Custom colors: array of hex strings, e.g. ['#0d9488', '#0891b2']
    colors: {
      type: Array,
      default: null,
    },
  },
  setup(props) {
    const chartEl = ref(null)
    const chart = ref(null)
    const googleRef = ref(null)

    const hasData = computed(() => Array.isArray(props.data) && props.data.length > 0)

    const buildDataTable = () => {
      if (!googleRef.value || !hasData.value) return null
      const google = googleRef.value
      const dataTable = new google.visualization.DataTable()
      dataTable.addColumn('string', 'Category')
      const hasSeries = Array.isArray(props.series) && props.series.length > 0

      if (hasSeries) {
        props.series.forEach((s) => {
          const label = s && (s.label || s.key) ? (s.label || s.key) : 'Value'
          dataTable.addColumn('number', String(label))
        })

        const rows = props.data.map((row) => {
          const category = row[props.categoryKey] || 'Unknown'
          const seriesValues = props.series.map((s) => {
            const key = s && s.key
            const raw = key ? row[key] : 0
            const value = Number(raw ?? 0)
            return isNaN(value) ? 0 : value
          })
          return [String(category), ...seriesValues]
        })
        dataTable.addRows(rows)
        // Format bar values and tooltips for each series
        if (props.yAxisFormatter && google.visualization && google.visualization.NumberFormat) {
          for (let c = 1; c <= props.series.length; c++) {
            for (let i = 0; i < dataTable.getNumberOfRows(); i++) {
              const val = dataTable.getValue(i, c);
              dataTable.setFormattedValue(i, c, props.yAxisFormatter(val));
            }
          }
        }
      } else {
        dataTable.addColumn('number', props.valueLabel || 'Value')

        const rows = props.data.map((row) => {
          const category = row[props.categoryKey] || 'Unknown'
          const value = Number(row[props.valueKey] ?? 0)
          return [String(category), isNaN(value) ? 0 : value]
        })
        dataTable.addRows(rows)
        // Format bar values and tooltips for single series
        if (props.yAxisFormatter && google.visualization && google.visualization.NumberFormat) {
          for (let i = 0; i < dataTable.getNumberOfRows(); i++) {
            const val = dataTable.getValue(i, 1);
            dataTable.setFormattedValue(i, 1, props.yAxisFormatter(val));
          }
        }
      }
      return dataTable
    }

    const drawChart = async () => {
      if (!chartEl.value || !hasData.value) return

      try {
        if (!googleRef.value) {
          googleRef.value = await loadGoogleCharts()
        }
        const google = googleRef.value
        const dataTable = buildDataTable()
        if (!dataTable) return

        const isHorizontal = props.orientation === 'bar'
        const hasSeries = Array.isArray(props.series) && props.series.length > 0

        if (!chart.value) {
          chart.value = isHorizontal
            ? new google.visualization.BarChart(chartEl.value)
            : new google.visualization.ColumnChart(chartEl.value)
        }

        const baseOpts = {
          legend: { position: hasSeries ? 'top' : 'none' },
          chartArea: isHorizontal ? { left: 160, top: 10, right: 20, bottom: 40 } : { left: 50, top: 10, right: 10, bottom: 50 },
          hAxis: isHorizontal ? { minValue: 0, gridlines: { count: 0 } } : { slantedText: true, slantedTextAngle: 30, gridlines: { count: 0 } },
          vAxis: isHorizontal
            ? { gridlines: { count: 0 },
                format: props.yAxisFormatter ? '' : undefined,
                viewWindow: { min: 0 },
                ticks: undefined,
                textStyle: { fontSize: 12 },
              }
            : {
                minValue: 0,
                gridlines: { count: 0 },
                format: props.yAxisFormatter ? '' : undefined,
                viewWindow: { min: 0 },
                ticks: undefined,
                textStyle: { fontSize: 12 },
              },
          isStacked: hasSeries,
          height: props.height
            ? props.height
            : isHorizontal
              ? Math.max(300, props.data.length * 32)
              : 260,
        }
        if (Array.isArray(props.colors) && props.colors.length > 0) {
          baseOpts.colors = props.colors
        }
        const options = baseOpts

        // If formatter provided, apply to vAxis
        if (props.yAxisFormatter && google.visualization && google.visualization.NumberFormat) {
          const formatter = new google.visualization.NumberFormat({
            pattern: '#,###',
            // The actual string formatting will be handled below
          });
          formatter.format = function (dataTable, col) {
            // Override: apply custom formatter to each value
            for (let i = 0; i < dataTable.getNumberOfRows(); i++) {
              const val = dataTable.getValue(i, col);
              dataTable.setFormattedValue(i, col, props.yAxisFormatter(val));
            }
          };
          // For single series
          if (!hasSeries) {
            formatter.format(dataTable, 1);
          } else {
            // For each series column
            for (let c = 1; c <= props.series.length; c++) {
              formatter.format(dataTable, c);
            }
          }
        }

        chart.value.draw(dataTable, options)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to render BD bar chart', e)
      }
    }

    onMounted(() => {
      drawChart()
    })

    watch(
      () => [props.data, props.valueKey, props.categoryKey, props.colors],
      () => {
        drawChart()
      },
      { deep: true },
    )

    onBeforeUnmount(() => {
      chart.value = null
    })

    return {
      chartEl,
      hasData,
    }
  },
}
</script>

<style scoped>
.bd-bar-chart-wrapper {
  width: 100%;
  min-height: 260px;
}

.bd-bar-chart {
  width: 100%;
  height: 260px;
}
</style>
